import type React from "react"
import type { SubmitHandler } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"

import { Box, Fade, Skeleton, Typography } from "@mui/material"

import { If } from "../if/if"
import { WhiteBox } from "../whiteBox"
import { ResetPasswordForm } from "./reset-password-form/reset-password-form"
import type { ResetPasswordSchema } from "./reset-password-form/reset-password-form.schema"
import { ResetPasswordInvalid } from "./reset-password-invalid/reset-password-invalid"
import { ResetPasswordSuccess } from "./reset-password-success/reset-password-success"
import {
  useGetUserByPasswordResetToken,
  useUpdateUserPassword,
} from "./reset-password-utils"

export const ResetPassword: React.FC = () => {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()
  const {
    passwordUserData,
    passwordUserIsError,
    passwordUserIsFetched,
    passwordUserIsFetching,
  } = useGetUserByPasswordResetToken(token)
  const {
    passwordUpdateIsPending,
    passwordUpdateIsSuccess,
    passwordUpdateMutate,
  } = useUpdateUserPassword()

  const isTokenValidationLoading =
    Boolean(token) && !passwordUserIsFetched && passwordUserIsFetching
  const isTokenValidationError = Boolean(token) && passwordUserIsError
  const isTokenValid = Boolean(passwordUserData)
  const isTokenInvalid =
    Boolean(token) &&
    passwordUserIsFetched &&
    !isTokenValid &&
    !passwordUserIsError

  const handleSubmit: SubmitHandler<ResetPasswordSchema> = (value): void => {
    if (token && value.password) {
      passwordUpdateMutate({ token, password: value.password })
    }
  }

  return (
    <Fade in={true}>
      <div>
        <WhiteBox aria-labelledby={"resetPasswordHeader"}>
          <Typography
            id="resetPasswordHeader"
            variant="h1"
            sx={{
              mb: 4,
              textAlign: "center",
            }}
          >
            {t("resetPassword.title")}
          </Typography>

          <If condition={isTokenValidationLoading}>
            <Box
              sx={{
                py: 6,
              }}
            >
              <Skeleton
                data-testid="reset-password-token-validation-skeleton"
                height={96}
                variant="rounded"
                width="100%"
              />
            </Box>
          </If>

          <If condition={isTokenValidationError}>
            <Typography
              sx={{
                textAlign: "center",
              }}
            >
              {t("resetPassword.tokenValidationErrorMessage")}
            </Typography>
          </If>

          <If condition={isTokenInvalid}>
            <ResetPasswordInvalid />
          </If>

          <If
            condition={
              passwordUserIsFetched && isTokenValid && !passwordUpdateIsSuccess
            }
          >
            <ResetPasswordForm
              isSubmitting={passwordUpdateIsPending}
              onSubmit={handleSubmit}
              user={passwordUserData}
            />
          </If>

          <If condition={passwordUserIsFetched && passwordUpdateIsSuccess}>
            <Fade in={passwordUpdateIsSuccess}>
              <div>
                <ResetPasswordSuccess />
              </div>
            </Fade>
          </If>
        </WhiteBox>
      </div>
    </Fade>
  )
}
